<template>
  <form
    method="POST"
    :action="postRegister"
    id="registration-form"
    class="md:mt-10 md:mb-16"
  >
    <input type="hidden" name="_token" :value="csrf" />
    <div class="flex flex-row">
      <div class="flex flex-col w-1/4"></div>
      <div class="flex flex-col w-2/4">
        <div class="flex flex-row">
          <div class="text-center w-full text-xs text-gray-500">
            Already have an account?
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-row mt-3">
      <div class="flex-col w-5/20 sm:w-7/20 md:w-5/20 md:w-7/20"></div>
      <div class="flex flex-col w-10/20 md:w-6/20">
        <a
          :href="'/login'"
          class="py-2 w-full text-center text-sm rounded-full bg-blue-400 text-white hover:text-white hover:bg-blue-500 focus:outline-none"
        >
          Login
        </a>
      </div>
      <div class="flex-col 1/4 sm:w-1/3"></div>
    </div>

    <div
      class="border-b-2 flex justify-center border-gray-300 mt-8 md:mt-10 mx-10 md:mx-16"
    ></div>

    <div class="flex flex-row">
      <div class="flex-col w-1/12 md:w-1/5"></div>
      <div class="flex-col w-10/12 sm:w-5/6 md:w-3/5">
        <div
          class="justify-center text-center mt-8 md:mt-10 text-xl text-gray-700"
        >
          <slot :item="item"></slot>
        </div>
      </div>
      <div class="flex-col md:w-1/5"></div>
    </div>

    <div class="flex flex-row">
      <div class="flex-col w-12/12 md:w-10/12 mt-6 md:mt-8 mx-auto">
        <!-- NZBN input -->
        <input type="hidden" id="nzbn" name="nzbn" v-model="forms.nzbn" />

        <transition name="fade">
          <div
            class="flex flex-row sm:pl-2 md:pl-0"
            v-show="isCompanyNameLoading"
          >
            <div class="flex flex-col w-5/20 md:w-6/20 lg:w-7/20"></div>
            <div
              class="flex flex-col ml-12 md:ml-3 w-full md:w-6/12 lg:w-8/12 text-blue-400 text-xs"
            >
              Loading data...
            </div>
          </div>
        </transition>

        <!-- Start of company legal name -->
        <div class="flex flex-row md:pr-0">
          <div
            class="flex flex-col pt-2 md:pt-1 w-8/20 md:w-1/3 md:pr-6 text-right text-sm md:text-base text-gray-600"
          >
            <div class="inline-flex tooltip">
              Company Legal Name
              <svg
                class="inline-block fill-current h-4 -mt-1 ml-1 w-4"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.3431 13.657A8 8 0 1113.656 2.343 8 8 0 012.343 13.657zm10.19-1.123a6.401 6.401 0 10-9.069-9.036 6.401 6.401 0 009.069 9.036zM7.199 8.801v-1.6h1.6v4.8H7.2v-3.2zm0-4.8h1.6v1.6H7.2v-1.6z"
                />
              </svg>
              <span
                class="tooltip-bottom tooltiptext text-left absolute bg-gray-700 text-white rounded-md text-md mt-10 -ml-8 tooltip-1-width z-10"
                style="min-width:240px"
              >
                Begin typing your company legal name and select from the
                dropdown options. <br />
                If your business is not a registered company, please contact us
                on (09) 2803639.
              </span>
            </div>
          </div>

          <div class="flex flex-col w-full md:w-2/3">
            <input
              @blur="validateForm($event)"
              @focus="formFocus($event)"
              name="company_name"
              v-model="forms.companyName"
              id="company_name"
              class="form-input ml-5 md:ml-2 md:ml-0 py-2 md:py-1 px-4"
              type="text"
              placeholder="Johns Company Ltd"
              @keyup="companyNameKeyup($event)"
              ref="companyName"
            />
            <div class="autocomplete ml-2">
              <ul v-show="isAutocompleteOpen" class="autocomplete-results">
                <li
                  v-for="(result, i) in availSearch"
                  :key="i"
                  class="autocomplete-result"
                  @click="setCompanyNameResult(result)"
                >
                  {{ result.label }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Company legal name validation -->
        <!-- This uses nzbn as nzbn is tied to the company legal name -->
        <div
          id="nzbn_error_server"
          class="flex flex-row sm:pl-2 md:pl-0"
          v-show="errors.isErrorNzbn"
        >
          <div class="flex flex-col w-5/20 md:w-6/20 lg:w-7/20"></div>
          <div
            class="flex flex-col ml-12 md:ml-3 w-full md:w-6/12 lg:w-8/12 text-red-500 text-xs"
            v-html="errorDisplayNzbn"
          ></div>
        </div>

        <div
          id="company_name_error_server"
          class="flex flex-row sm:pl-2 md:pl-0"
          v-show="errors.isErrorCompanyName"
        >
          <div class="flex flex-col w-5/20 md:w-6/20 lg:w-7/20"></div>
          <div
            class="flex flex-col ml-12 md:ml-3 w-full md:w-6/12 lg:w-8/12 text-red-500 text-xs"
            v-html="errorDisplayCompanyName"
          ></div>
        </div>

        <transition name="fade">
          <div
            id="company_name_hint"
            class="flex flex-row sm:pl-2 md:pl-0"
            v-show="hints.isCompanyNameHintOpen"
          >
            <div class="flex flex-col w-5/20 md:w-6/20 lg:w-7/20"></div>
            <div
              class="flex flex-col ml-12 md:ml-3 w-full md:w-6/12 lg:w-8/12 text-xs"
              :class="
                isClientValidationShowing ? 'text-red-500' : 'text-gray-500'
              "
            >
              Please begin typing your company legal name and then select your
              company from the dropdown options
            </div>
          </div>
        </transition>
        <!-- End of company legal name -->

        <!-- Start of Business Trading Name -->
        <div class="flex flex-row mt-4 sm:pl-2 md:pl-0">
          <div
            class="flex flex-col pt-2 md:pt-1 w-8/20 md:w-1/3 md:pr-6 text-right text-sm md:text-base text-gray-600"
          >
            <span>
              Business Trading Name
              <span class="text-red-500">*</span>
            </span>
          </div>
          <div class="flex flex-col w-full md:w-2/3">
            <input
              @focus="formFocus($event)"
              @blur="validateForm($event)"
              name="business_name"
              v-model="forms.businessName"
              id="business_name"
              class="form-input ml-5 md:ml-2 md:ml-0 py-2 md:py-1 px-4"
              type="text"
              placeholder="Johns Trade Name"
            />
          </div>
        </div>

        <!-- Business Trading Name validation -->
        <transition name="fade">
          <div
            id="business_name_error_server"
            v-show="errors.isErrorBusinessName"
            class="flex flex-row sm:pl-2 md:pl-0"
          >
            <div class="flex flex-col w-5/20 md:w-6/20 lg:w-7/20"></div>
            <div
              class="flex flex-col ml-12 md:ml-3 w-full md:w-6/12 lg:w-8/12 text-red-500 text-xs"
              v-html="errorDisplayBusinessName"
            ></div>
          </div>
        </transition>
        <!-- End of Business Trading Name -->

        <!-- Start of name -->
        <transition name="fade">
          <div id="your_name_hint" v-show="hints.isYourNameHintOpen">
            <div class="flex flex-row mt-4 -mb-4 ml-4">
              <div class="flex-col w-1/3"></div>
              <div class="text-left text-xs md:pl-1 text text-gray-500 w-1/3">
                First Name
              </div>
              <div class="text-left md:pl-1 text-xs text-gray-500">
                Last Name
              </div>
            </div>
          </div>
        </transition>

        <div class="flex flex-row mt-4 sm:pl-2 md:pl-0 name-fields">
          <div
            class="flex flex-col pt-2 md:pt-1 w-1/3 md:w-1/3 pr-4 md:pr-6 text-right text-sm md:text-base text-gray-600"
          >
            <span>
              Your Name
              <span class="text-red-500">*</span>
            </span>
          </div>

          <div class="flex flex-col md:pl-2 w-1/3 ">
            <input
              class="form-input py-2 md:py-1 pl-3 md:px-4 mr-1"
              name="first_name"
              type="text"
              placeholder="John"
              id="first_name"
              v-model="forms.firstName"
              @blur="validateForm($event)"
              @focus="formFocus($event)"
            />
          </div>

          <div class="flex flex-col w-1/3 ">
            <input
              class="form-input py-2 md:py-1 pl-3 md:px-4 mr-1"
              name="last_name"
              type="text"
              placeholder="Doe"
              id="last_name"
              v-model="forms.lastName"
              @blur="validateForm($event)"
              @focus="formFocus($event)"
            />
          </div>
        </div>

        <transition name="fade">
          <div
            id="your_name_error_server"
            v-show="errors.isErrorFirstName || errors.isErrorLastName"
            class="flex flex-row sm:pl-2 md:pl-0 ml-4"
          >
            <div class="flex-col w-1/3"></div>
            <div
              class="text-left text-red-500 text-xs md:pl-1 w-1/3"
              v-html="errorDisplayFirstName"
            ></div>
            <div
              class="text-left text-red-500 text-xs md:pl-1"
              v-html="errorDisplayLastName"
            ></div>
          </div>
        </transition>
        <!-- End of name -->

        <!-- Start of phone number -->
        <div class="flex flex-row mt-4 sm:pl-2 md:pl-0">
          <div
            class="flex flex-col pt-2 md:pt-1 w-8/20 md:w-1/3 md:pr-6 text-right text-sm md:text-base text-gray-600"
          >
            <span>
              Your Mobile Number
              <span class="text-red-500">*</span>
            </span>
          </div>
          <div class="flex flex-col w-full md:w-2/3">
            <input
              @blur="validateForm($event)"
              @focus="formFocus($event)"
              id="business_phone"
              name="business_phone"
              v-model="forms.businessPhone"
              class="form-input ml-5 md:ml-2 md:ml-0 py-2 md:py-1 px-4"
              type="text"
              placeholder="0212344567"
            />
          </div>
        </div>

        <!-- Phone number validation -->
        <transition name="fade">
          <div
            id="business_phone_error"
            class="flex flex-row sm:pl-2 md:pl-0"
            v-show="isBusinessPhoneValidated"
          >
            <div class="flex flex-col w-5/20 md:w-6/20 lg:w-7/20"></div>
            <div
              class="flex flex-col ml-12 md:ml-3 w-full md:w-6/12 lg:w-8/12 text-red-500 text-xs"
            >
              This isn't a real phone number
            </div>
          </div>
        </transition>

        <transition name="fade">
          <div
            id="business_phone_error_server"
            v-show="errors.isErrorBusinessPhone"
            class="flex flex-row sm:pl-2 md:pl-0"
          >
            <div class="flex flex-col w-5/20 md:w-6/20 lg:w-7/20"></div>
            <div
              class="flex flex-col ml-12 md:ml-3 w-full md:w-6/12 lg:w-8/12 text-red-500 text-xs"
              v-html="errorDisplayBusinessPhone"
            ></div>
          </div>
        </transition>
        <!-- End of phone number -->

        <!-- Start of business email -->
        <div class="flex flex-row mt-4 sm:pl-2 md:pl-0">
          <div
            class="flex flex-col pt-2 md:pt-1  w-8/20 md:w-1/3 md:pr-6 text-right text-sm md:text-base text-gray-600"
          >
            <span>
              Business Email
              <span class="text-red-500">*</span>
            </span>
          </div>
          <div class="flex flex-col w-full md:w-2/3">
            <input
              @focus="formFocus($event)"
              @blur="validateForm($event)"
              class="form-input ml-5 md:ml-2 md:ml-0 py-2 md:py-1 px-4"
              type="text"
              placeholder="john@doe.com"
              id="email"
              name="email"
              v-model="forms.email"
            />
          </div>
        </div>

        <transition name="fade">
          <div id="email_hint" v-show="hints.isBusinessEmailHintOpen">
            <div class="flex flex-row ml-4">
              <div class="flex-col w-1/3"></div>
              <div class="text-left text-xs text md:ml-3 text-gray-500 w-2/3">
                You will use this to login to Freighthub
              </div>
            </div>
          </div>
        </transition>

        <transition name="fade">
          <div
            id="email_error"
            class="flex flex-row sm:pl-2 md:pl-0"
            v-show="isEmailValidated"
          >
            <div class="flex flex-col w-5/20 md:w-6/20 lg:w-7/20"></div>
            <div
              class="flex flex-col ml-12 md:ml-3 w-full md:w-6/12 lg:w-8/12 text-red-500 text-xs"
            >
              This isn't a real email address
            </div>
          </div>
        </transition>

        <transition name="fade">
          <div
            id="email_error_server"
            v-show="errors.isErrorEmail"
            class="flex flex-row sm:pl-2 md:pl-0"
          >
            <div class="flex flex-col w-5/20 md:w-6/20 lg:w-7/20"></div>
            <div
              class="flex flex-col ml-12 md:ml-3 w-full md:w-6/12 lg:w-8/12 text-red-500 text-xs"
              v-html="errorDisplayEmail"
            ></div>
          </div>
        </transition>
        <!-- End of business email -->

        <!-- Start of password -->
        <div class="flex flex-row mt-4 sm:pl-2 md:pl-0">
          <div
            class="flex flex-col pt-2 md:pt-1  w-8/20 md:w-1/3 md:pr-6 text-right text-sm md:text-base text-gray-600"
          >
            <span>
              Password
              <span class="text-red-500">*</span>
            </span>
          </div>
          <div class="flex flex-col w-full md:w-2/3 relative">
            <input
              @blur="validateForm($event)"
              @focus="formFocus($event)"
              class="form-input ml-5 md:ml-2 md:ml-0 py-2 md:py-1 px-4"
              type="password"
              name="password"
              id="password"
              v-model="forms.password"
            />
          </div>
          <input
            type="hidden"
            name="password_confirmation"
            v-model="forms.password"
          />
        </div>

        <!-- Password validation -->
        <transition name="fade">
          <div id="password_hint" v-show="hints.isPasswordHintOpen">
            <div class="flex flex-row ml-4">
              <div class="flex-col w-1/3"></div>
              <div class="text-left text-xs text md:ml-3 text-gray-500 w-2/3">
                Must be least 6 characters
              </div>
            </div>
          </div>
        </transition>

        <transition name="fade">
          <div
            id="password_error"
            class="flex flex-row sm:pl-2 md:pl-0"
            v-show="isPasswordValidated"
          >
            <div class="flex flex-col w-5/20 md:w-6/20 lg:w-7/20"></div>
            <div
              class="flex flex-col ml-12 md:ml-3 w-full md:w-6/12 lg:w-8/12 text-red-500 text-xs"
            >
              Your password must be at least 6 characters
            </div>
          </div>
        </transition>

        <transition name="fade">
          <div
            id="password_error_server"
            v-show="errors.isErrorPassword"
            class="flex flex-row sm:pl-2 md:pl-0"
          >
            <div class="flex flex-col w-5/20 md:w-6/20 lg:w-7/20"></div>
            <div
              class="flex flex-col ml-12 md:ml-3 w-full md:w-6/12 lg:w-8/12 text-red-500 text-xs"
              v-html="errorDisplayPassword"
            ></div>
          </div>
        </transition>
        <!-- End of password -->

        <!-- Start of What Do You Do? -->
        <div class="flex flex-row md: md:ml-5 mt-6 sm:pl-2 md:pl-0">
          <div
            class="flex flex-col pt-2 md:pt-1 w-8/20 md:w-1/3 pr-9 text-right text-sm md:text-base text-gray-600"
          >
            <span>
              What do you do?
              <span class="text-red-500">*</span>
            </span>
          </div>

          <div
            class="flex flex-col w-full md:w-2/3 ml-6 md:ml-0 text-base text-gray-600 relative"
          >
            <div class="flex flex-row text-right relative">
              <div class="flex flex-col w-full relative">
                <!-- Toggle Button -->
                <label class="flex items-center cursor-pointer toggle">
                  <!-- toggle -->
                  <div class="relative">
                    <!-- input -->
                    <input
                      value="on"
                      id="send_freight"
                      name="send_freight"
                      type="checkbox"
                      class="hidden toggle"
                      v-model="forms.sendFreight"
                      :checked="isSendFreightChecked"
                      @click="formFocus($event)"
                    />
                    <!-- line -->
                    <div
                      class="toggle__line w-9 h-5 bg-gray-200 rounded-full shadow-inner"
                    ></div>
                    <!-- dot -->
                    <div
                      class="toggle__dot absolute w-4 h-4 dot_test bg-white rounded-full inset-y-0 left-0"
                    ></div>
                  </div>
                  <!-- label -->
                  <div class="ml-6 w-full flex relative text-left">
                    <div class="inline-block flex-col">
                      Send Freight
                    </div>
                    <a>
                      <div
                        class="tooltip inline-block pt- pl-5 text-gray-500 hover:text-blue-400"
                      >
                        <svg
                          class="hidden fill-current h-4 w-4 mt-1"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M2.3431 13.657A8 8 0 1113.656 2.343 8 8 0 012.343 13.657zm10.19-1.123a6.401 6.401 0 10-9.069-9.036 6.401 6.401 0 009.069 9.036zM7.199 8.801v-1.6h1.6v4.8H7.2v-3.2zm0-4.8h1.6v1.6H7.2v-1.6z"
                          />
                        </svg>
                        <span
                          class="tooltiptext hidden bg-gray-700 text-white rounded-md text-xs z-50"
                          >Testing - Sender</span
                        >
                      </div>
                    </a>
                  </div>
                </label>
              </div>
            </div>

            <div class="flex flex-row  text-right pt-4">
              <div class="flex flex-col w-full">
                <!-- Toggle Button -->
                <label class="flex items-center cursor-pointer toggle">
                  <!-- toggle -->
                  <div class="relative">
                    <!-- input -->
                    <input
                      value="on"
                      id="carry_freight"
                      name="carry_freight"
                      type="checkbox"
                      class="hidden toggle"
                      v-model="forms.carryFreight"
                      :checked="isCarryFreightChecked"
                      @click="formFocus($event)"
                    />
                    <!-- line -->
                    <div
                      class="toggle__line w-9 h-5 bg-gray-200 rounded-full shadow-inner"
                    ></div>
                    <!-- dot -->
                    <div
                      class="toggle__dot absolute w-4 h-4 dot_test bg-white rounded-full inset-y-0 left-0"
                    ></div>
                  </div>
                  <!-- label -->
                  <div class="ml-6 w-full flex relative text-left">
                    <div class="inline-block flex-col">
                      Carry Freight
                    </div>
                    <a>
                      <div
                        class="tooltip inline-block pl-4 text-gray-500 hover:text-blue-400"
                      >
                        <svg
                          class="hidden fill-current h-4 mt-1 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M2.3431 13.657A8 8 0 1113.656 2.343 8 8 0 012.343 13.657zm10.19-1.123a6.401 6.401 0 10-9.069-9.036 6.401 6.401 0 009.069 9.036zM7.199 8.801v-1.6h1.6v4.8H7.2v-3.2zm0-4.8h1.6v1.6H7.2v-1.6z"
                          />
                        </svg>
                        <span
                          class="tooltiptext hidden bg-gray-700 text-white rounded-md text-xs z-50"
                          >Testing - Carrier</span
                        >
                      </div>
                    </a>
                  </div>
                </label>
              </div>
            </div>

            <transition name="fade">
              <div
                class="flex flex-row sm:pl-2 md:pl-0 my-1"
                v-show="isRoleChecked"
              >
                <div class="flex flex-col w-full pl-1 text-red-500 text-xs">
                  Please choose whether you are freight sender or carrier or
                  both
                </div>
              </div>
            </transition>
            <transition name="fade">
              <div
                v-show="errors.isErrorSender"
                class="flex flex-row sm:pl-2 md:pl-0 my-1"
              >
                <div
                  class="flex flex-col w-full pl-1 text-red-500 text-xs"
                  v-html="errorDisplaySender"
                ></div>
              </div>
            </transition>
            <transition name="fade">
              <div
                v-show="errors.isErrorCarrier"
                class="flex flex-row sm:pl-2 md:pl-0 my-1"
              >
                <div
                  class="flex flex-col w-full pl-1 text-red-500 text-xs"
                  v-html="errorDisplayCarrier"
                ></div>
              </div>
            </transition>
          </div>
        </div>

        <!-- Start Terms -->
        <div class="flex flex-row pt-4 ml-6 md:ml-0 sm:pl-2 md:pl-0">
          <div
            class="flex-col w-1/3 md:pr-6 pt-2 md:pt-1 text-right text-sm md:text-base text-gray-600"
          ></div>

          <div
            class="flex flex-col w-full md:w-2/3 ml-6 md:ml-0 text-base text-gray-600 relative"
          >
            <div class="flex flex-row text-right relative">
              <div class="flex flex-col w-full relative">
                <!-- Toggle Button -->
                <label class="flex items-center cursor-pointer toggle">
                  <!-- toggle -->
                  <div class="ml-3 relative">
                    <!-- input -->
                    <input
                      id="terms"
                      name="terms"
                      v-model="forms.terms"
                      type="checkbox"
                      class="hidden toggle"
                      @click="formFocus($event)"
                    />
                    <!-- line -->
                    <div
                      class="toggle__line w-9 h-5 bg-gray-200 rounded-full shadow-inner"
                    ></div>
                    <!-- dot -->
                    <div
                      class="toggle__dot absolute w-4 h-4 dot_test bg-white rounded-full inset-y-0 left-0"
                    ></div>
                  </div>
                  <!-- label -->
                  <div class="ml-6 w-full flex relative text-left">
                    <div class="inline-block flex-col">
                      I have read and agree with Freighthub's
                      <a
                        href="/terms"
                        target="_blank"
                        class="text-blue-400 hover:text-blue-600"
                        >terms and conditions</a
                      >
                      <span class="text-red-500">*</span>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <transition name="fade">
              <div
                id="terms-validation"
                v-show="errors.isErrorTerms"
                class="flex flex-row sm:pl-2 md:pl-0"
              >
                <div
                  class="flex flex-col pl-2 ml-12 md:ml-3 w-full text-red-500 text-xs"
                  v-html="errorDisplayTerms"
                ></div>
              </div>
            </transition>
          </div>
        </div>
        <!-- End Terms -->

        <!-- Start of how did you find us -->
        <div class="flex flex-row pt-2 ml-6 md:ml-0 sm:pl-2 md:pl-0">
          <div
            class="flex-col w-1/3 md:pr-6 pt-2 md:pt-1 text-right text-sm md:text-base text-gray-600"
          >
            How did you find us?
          </div>

          <div
            id="options"
            class="options-form flex flex-col w-full md:w-2/3"
            v-if="isMainOptionActive"
          >
            <div class="inline-block mr-6 md:mr-2">
              <a
                id="options_box"
                @click="isDropdownOpen = !isDropdownOpen"
                @blur="isDropdownOpen = false"
                tabindex="0"
                class="relative block w-full bg-gray-200 text-sm text-gray-500 border border-gray-200 px-4 py-3 ml-5 md:ml-2
                  md:py-2 rounded-t-md rounded-b-md leading-tight focus:outline-none hover:text-gray-600"
              >
                <div id="options_value">
                  <span v-if="!isHeardFromSelected && isMainOptionActive">
                    Select One
                  </span>
                  <span v-else>
                    {{ forms.heardFrom }}
                  </span>
                </div>

                <div
                  class="absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
                >
                  <svg
                    id="options_arrow"
                    class="fill-current h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                  >
                    <path
                      d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                    />
                  </svg>
                </div>
              </a>

              <transition name="fade">
                <div
                  id="dropdown"
                  v-show="isDropdownOpen"
                  class="rounded-md absolute w-12/20 ml-5 pr-2 sm:pr-0 md:ml-2 lg:pr-5 xl:pr-0 dropdown-width"
                >
                  <a
                    v-for="(option, idx) in heardFromOptions"
                    :key="idx"
                    @click="setHeardFromResult(option)"
                  >
                    <div
                      class="text-xs pl-4 py-1 text-gray-500 bg-white hover:bg-gray-200 border-gray-200"
                      :class="{
                        'border-b': idx !== heardFromOptions.length - 1,
                        'rounded-b-md': idx === heardFromOptions.length - 1
                      }"
                    >
                      {{ option }}
                    </div>
                  </a>
                </div>
              </transition>
            </div>
          </div>

          <div
            id="options-other"
            class="options-form flex flex-col w-full md:w-2/3 pr-5 md:pr-2"
            v-else
          >
            <div class="block relative w-full">
              <input
                id="options-input"
                class="w-full bg-gray-200 rounded-md text-sm text-gray-800 ml-5 md:ml-2 md:ml-0 py-2 md:py-1 px-4 focus:bg-white border border-gray-200 focus:outline-none focus:border-blue-400"
                type="text"
                ref="optionsOther"
                v-model="forms.heardFrom"
              />
              <a
                id="options-other-arrow"
                @click="otherOptionClickHandler"
                class=" absolute inset-y-0 right-0 flex items-center  text-gray-700"
              >
                <svg
                  class="fill-current h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>

        <input
          type="text"
          name="heard_from"
          v-model="forms.heardFrom"
          class="hidden"
          id="heard_from"
          :readonly="isHeardFrom"
        />

        <div id="heard_from_error_server" class="flex flex-row sm:pl-2 md:pl-0">
          <div class="flex flex-col w-5/20 md:w-6/20 lg:w-7/20"></div>
          <div
            class="flex flex-col ml-12 md:ml-3 w-full md:w-6/12 lg:w-5/12 text-red-500 text-xs"
            v-html="errorDisplayHeardFrom"
          ></div>
        </div>
        <!-- End of how did you find us -->
      </div>
    </div>

    <div class="flex flex-row mt-10">
      <div class="flex flex-col w-1/4 sm:w-1/3"></div>
      <a class="flex flex-col w-1/2 sm:w-1/3">
        <button
          type="submit"
          id="submit-registration"
          class="py-4 w-full text-center text-sm rounded-full bg-teal-400 text-white hover:text-white hover:bg-teal-500 focus:outline-none"
        >
          Lets get started
        </button>
      </a>
    </div>
  </form>
  <!--Form end-->
</template>

<script>
import axios from "axios";

export default {
  name: "register",
  props: [
    "item",
    "isSendFreightChecked",
    "isCarryFreightChecked",
    "isHeardFrom",
    "postRegister",
    "csrf",
    "nzbn",
    "companyName",
    "businessName",
    "firstName",
    "lastName",
    "businessPhone",
    "email",
    "password",
    "terms",

    "errorDisplayNzbn",
    "errorDisplayCompanyName",
    "errorDisplayBusinessName",
    "errorDisplayFirstName",
    "errorDisplayLastName",
    "errorDisplayBusinessPhone",
    "errorDisplayEmail",
    "errorDisplayPassword",
    "errorDisplaySender",
    "errorDisplayCarrier",
    "errorDisplayHeardFrom",
    "errorDisplayTerms"
  ],
  data() {
    return {
      forms: {
        nzbn: null,
        companyName: null,
        businessName: null,
        firstName: null,
        lastName: null,
        businessPhone: null,
        email: null,
        password: null,
        sendFreight: true,
        carryFreight: false,
        terms: false,
        heardFrom: null
      },
      autocompleteResults: [],
      isDropdownOpen: false,
      isAutocompleteOpen: false,
      isMainOptionActive: true,
      isBusinessPhoneValidated: false,
      isEmailValidated: false,
      isPasswordValidated: false,
      isCompanyNameValidated: false,
      isHeardFromSelected: false,
      isClientValidationShowing: false,
      isCompanyNameLoading: false,
      isRoleChecked: false,
      hints: {
        isYourNameHintOpen: false,
        isCompanyNameHintOpen: true,
        isBusinessNameHintOpen: false,
        isBusinessEmailHintOpen: false,
        isPasswordHintOpen: false
      },
      errors: {
        isErrorNzbn: true,
        isErrorCompanyName: true,
        isErrorBusinessName: true,
        isErrorFirstName: true,
        isErrorLastName: true,
        isErrorBusinessPhone: true,
        isErrorEmail: true,
        isErrorPassword: true,
        isErrorSender: true,
        isErrorCarrier: true,
        isErrorTerms: true,
        isErrorHeardFrom: true
      },
      heardFromOptions: ["Word of Mouth", "Online", "Advert", "Other"]
    };
  },

  mounted() {
    this.$refs.companyName.focus();
  },

  created() {
    this.forms.nzbn = this.nzbn;
    this.forms.companyName = this.companyName;
    this.forms.businessName = this.businessName;
    this.forms.firstName = this.firstName;
    this.forms.lastName = this.lastName;
    this.forms.businessPhone = this.businessPhone;
    this.forms.email = this.email;
    this.forms.password = this.password;
    this.forms.sendFreight = this.isSendFreightChecked;
    this.forms.carryFreight = this.isCarryFreightChecked;
    this.forms.terms = this.terms;
  },

  methods: {
    /*
     * Business name suggestions section
     */
    getCompanyAutocompleteSuggestions() {
      let name = this.forms.companyName;

      // Get name suggestions using current value in input
      return axios
        .get("/api/name-suggestions", {
          params: {
            name: name
          }
        })
        .then(res => {
          // Process response, autocomplete docs require a value and a label
          let suggestedItems = res.data.items;
          let nameSuggestions = [];

          for (let key in suggestedItems) {
            if (suggestedItems.hasOwnProperty(key)) {
              let suggestedItem = suggestedItems[key];

              nameSuggestions.push({
                index: key,
                label: suggestedItem["entityName"],
                value: suggestedItem["entityName"],
                nzbn: suggestedItem["nzbn"],
                business_name: suggestedItem["tradingNames"].length
                  ? suggestedItem["tradingNames"][0]
                  : ""
              });
            }
          }
          return nameSuggestions;
        })
        .catch(() => {
          return [];
        });
    },

    companyNameKeyup(e) {
      this.errors.isErrorNzbn = false;
      this.errors.isErrorCompanyName = false;
      this.isClientValidationShowing = false;
      this.isCompanyNameLoading = true;
      let companyName = this.forms.companyName;

      // Validation before request
      if (companyName === "") {
        this.forms.nzbn = null;
        this.isAutocompleteOpen = false;
        this.isCompanyNameLoading = false;
        this.hints.isCompanyNameHintOpen = true;
        return;
      }

      if (companyName.length < 2) {
        this.isCompanyNameLoading = false;
        return;
      }

      if (e.key === "Backspace" || e.key === "Delete") {
        this.forms.nzbn = null;
      }

      this.getCompanyAutocompleteSuggestions().then(nameSuggestions => {
        this.autocompleteResults = [];
        if (nameSuggestions.length > 0) {
          Vue.set(this, "autocompleteResults", nameSuggestions);
          this.isAutocompleteOpen = true;
          this.isCompanyNameLoading = false;
        } else {
          this.isAutocompleteOpen = false;
          this.isCompanyNameLoading = false;
        }
      });
    },

    setCompanyNameResult(result) {
      this.forms.companyName = result.label;
      this.forms.nzbn = result.nzbn;
      this.forms.businessName =
        result.business_name === "" ? result.label : result.value;
      this.isAutocompleteOpen = false;
      this.isClientValidationShowing = false;
      this.hints.isCompanyNameHintOpen = false;
      // console.log("CLICKED");
    },

    setHeardFromResult(result) {
      this.isDropdownOpen = false;
      this.forms.heardFrom = null;

      if (result === "Other") {
        this.isMainOptionActive = false;
        this.$nextTick(() => {
          this.$refs.optionsOther.focus();
        });
      } else {
        this.isHeardFromSelected = true;
        this.forms.heardFrom = result;
      }
    },

    otherOptionClickHandler() {
      this.isMainOptionActive = true;
      this.isHeardFromSelected = false;
      this.isDropdownOpen = !this.isDropdownOpen;
    },

    validateForm(e) {
      let id = e.target.id;
      let value = e.target.value;

      if (id === "first_name" || id === "last_name") {
        this.hints.isYourNameHintOpen = !this.hints.isYourNameHintOpen;
      }
      if (id === "company_name") {
        if (this.forms.nzbn === null || this.forms.nzbn === "") {
          this.isClientValidationShowing = true;
          this.hints.isCompanyNameHintOpen = true;
        }
      }
      if (id === "business_name") {
        this.hints.isBusinessNameHintOpen = true;
      }
      if (id === "business_phone") {
        let isPhoneNumber = this.validatePhoneNumber(value);

        if (value && !isPhoneNumber) {
          this.isBusinessPhoneValidated = true;
        }
      }
      if (id === "email") {
        this.hints.isBusinessEmailHintOpen = false;
        let isEmail = this.validateEmail(value);

        if (value && !isEmail) {
          this.isEmailValidated = true;
        }
      }
      if (id === "password") {
        this.hints.isPasswordHintOpen = false;

        if (value && value.length < 6) {
          this.isPasswordValidated = true;
        }
      }
    },

    formFocus(e) {
      let id = e.target.id;
      let value = e.target.value;
      let carrier = this.forms.carryFreight;
      let sender = this.forms.sendFreight;

      if (id === "first_name" || id === "last_name") {
        this.hints.isYourNameHintOpen = !this.hints.isYourNameHintOpen;
        this.errors.isErrorFirstName = false;
        this.errors.isErrorLastName = false;
      }
      if (id === "company_name") {
        this.errors.isErrorCompanyName = false;
        this.hints.isCompanyNameHintOpen = true;

        if (this.forms.nzbn === "" || this.forms.nzbn === null) {
          this.hints.isCompanyNameHintOpen = true;
          if (
            this.forms.companyName === "" ||
            this.forms.companyName === null
          ) {
            this.hints.isCompanyNameHintOpen = true;
          }
        } else {
          this.hints.isCompanyNameHintOpen = false;
        }
      }
      if (id === "business_name") {
        this.errors.isErrorBusinessName = false;
      }
      if (id === "business_phone") {
        this.errors.isErrorBusinessPhone = false;
        this.isBusinessPhoneValidated = false;
      }
      if (id === "email") {
        this.errors.isErrorEmail = false;
        this.hints.isBusinessEmailHintOpen = true;
        this.isEmailValidated = false;
      }
      if (id === "password") {
        this.hints.isPasswordHintOpen = true;
        this.errors.isErrorPassword = false;
        this.isPasswordValidated = false;
      }
      if (id === "send_freight") {
        if (!carrier && !sender) {
          this.errors.isErrorCarrier = false;
          this.errors.isErrorSender = false;
          this.isRoleChecked = false;
        }
        if (!carrier && sender) {
          this.isRoleChecked = true;
        }
        if (carrier && !sender) {
          this.errors.isErrorCarrier = false;
          this.errors.isErrorSender = false;
          this.isRoleChecked = false;
        }
      }
      if (id === "carry_freight") {
        if (!carrier && !sender) {
          this.errors.isErrorCarrier = false;
          this.errors.isErrorSender = false;
          this.isRoleChecked = false;
        }
        if (carrier && !sender) {
          this.isRoleChecked = true;
        }
        if (!carrier && sender) {
          this.errors.isErrorCarrier = false;
          this.errors.isErrorSender = false;
          this.isRoleChecked = false;
        }
      }
      if (id === "terms") {
        this.errors.isErrorTerms = false;
      }
    },

    validateEmail(email) {
      var re = /^(([^<>()[\]\\.,;:\s@]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(email);
    },

    validatePhoneNumber(number) {
      let regex = /^[0-9]*$/;
      return regex.test(number);
    }
  },

  computed: {
    availSearch() {
      return this.autocompleteResults;
    }
  }
};
</script>

<style scoped>
.autocomplete {
  position: relative;
}

.autocomplete-results {
  padding: 0;
  margin: 0;
  border: 1px solid #eeeeee;
  min-height: 30px;
  max-height: 150px;
  width: 100%;
  overflow: auto;
  position: absolute;
  background-color: #ffffff;
}

.autocomplete-result {
  list-style: none;
  text-align: left;
  padding: 4px 8px;
  cursor: pointer;
}

.autocomplete-result:hover {
  background-color: #4399e1;
  color: white;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
